import { call, put, select, takeEvery, take, takeLatest, delay } from 'redux-saga/effects';
import {
    addFileAsyncActions, checkFilesAsyncAction,
    deleteFileAsyncActions,
    fetchAllFilesAsyncActions,
    fetchFileChildrenAsyncActions,
    fetchFileRootWithChildrenAsyncActions,
    IAddFileAsyncRequestPayload,
    IDeleteFileAsyncRequestPayload, IMoveFileAsyncRequestPayload,
    IRenameFileAsyncRequestPayload, moveFileAsyncActions,
    renameFileAsyncActions,
    selectFileAsyncAction
} from "./actions";
import { api } from "../../firebase";
import { fileSystemCollectionKey } from "../../firebase/api/files";
import { selectCurrentUser } from "../user/selectors";
import { IdObjectsWithId } from "../../utils";
import { FileType, ICheckedFileIds, IFile } from "./types";
import {
    selectCheckedFileIds,
    selectFileDataById,
    selectFiles,
} from "./selectors";
import { fileDataCollectionKey } from "../../firebase/api";
import { findAllCheckedIds } from "./utils";
import { modifyingActiveBannerTemplateAction } from "../banner/template/actions";
import { loadDataForFilesAsyncAction } from "./filedata/actions";

// Fetch all files info in the tree without file data.
function* fetchAllFilesSaga(action: ReturnType<typeof fetchAllFilesAsyncActions.request>) {
    try {
        // @ts-ignore
        const data = yield call(api.fetchCollection, fileSystemCollectionKey);
        yield put(fetchAllFilesAsyncActions.success({ files: data }))

    } catch (e: any) {
        yield put(fetchAllFilesAsyncActions.failure({ error: e }))
    }
}

function* fetchRootWithChildrenSaga(action: ReturnType<typeof fetchFileRootWithChildrenAsyncActions.request>) {
    try {
        // @ts-ignore
        const data = yield call(api.files.fetchRootWithChildren, fileSystemCollectionKey);
        yield put(fetchFileRootWithChildrenAsyncActions.success({ files: data }))
    } catch (e: any) {
        yield put(fetchFileRootWithChildrenAsyncActions.failure({ error: e }))
    }
}

function* fetchChildren(action: ReturnType<typeof fetchFileChildrenAsyncActions.request>) {
    try {
        // @ts-ignore
        const data = yield call(api.files.fetchChildren, fileSystemCollectionKey, action.payload.file);
        yield put(fetchFileChildrenAsyncActions.success({ files: data }));
        action.payload.resolve()
    } catch (e: any) {
        yield put(fetchFileChildrenAsyncActions.failure({ error: e }));
        action.payload.resolve()
    }
}

function* addFileSaga(action: ReturnType<typeof addFileAsyncActions.request>) {
    try {
        // @ts-ignore
        const user = yield select(selectCurrentUser);
        const params: IAddFileAsyncRequestPayload = {
            ...action.payload,
            user: user
        };
        // @ts-ignore
        const data = yield call(api.files.saveFileToCollection, fileSystemCollectionKey, fileDataCollectionKey, params);
        yield put(addFileAsyncActions.success({ file: data }))
    } catch (e: any) {
        yield put(addFileAsyncActions.failure({ error: e }))
    }
}

function* renameFileSaga(action: ReturnType<typeof renameFileAsyncActions.request>) {
    try {
        // @ts-ignore
        const user = yield select(selectCurrentUser);
        const params: IRenameFileAsyncRequestPayload = {
            ...action.payload,
            user: user
        };
        const data: IdObjectsWithId<IFile> = yield call(api.files.renameFile, fileSystemCollectionKey, params);
        yield put(renameFileAsyncActions.success({ files: data }))

    } catch (e: any) {
        yield put(renameFileAsyncActions.failure({ error: e }))
    }

}

function* moveFileSaga(action: ReturnType<typeof moveFileAsyncActions.request>) {
    try {
        // @ts-ignore
        const user = yield select(selectCurrentUser);
        const params: IMoveFileAsyncRequestPayload = {
            ...action.payload,
            user: user
        };
        const data: IdObjectsWithId<IFile> = yield call(api.files.moveFile, fileSystemCollectionKey, params);
        yield put(moveFileAsyncActions.success({ files: data }))

    } catch (e: any) {
        yield put(moveFileAsyncActions.failure({ error: e }))
    }

}

function* deleteFileSaga(action: ReturnType<typeof deleteFileAsyncActions.request>) {
    try {
        // @ts-ignore
        const user = yield select(selectCurrentUser);
        const params: IDeleteFileAsyncRequestPayload = {
            ...action.payload,
            user: user
        };
        // @ts-ignore
        const data = yield call(api.files.deleteFileFromCollection, fileSystemCollectionKey, fileDataCollectionKey, params);
        yield put(deleteFileAsyncActions.success({ file: data }))
    } catch (e: any) {
        yield put(deleteFileAsyncActions.failure({ error: e }))
    }
}

function* selectFileSaga(action: ReturnType<typeof selectFileAsyncAction.request>) {
    try {
        const file = action.payload.file;
        let fileData;
        if (file.fileDataId) {
            // @ts-ignore
            fileData = yield select(selectFileDataById, file.fileDataId);
            if (!fileData) {
                // @ts-ignore
                fileData = yield call(api.fetchDocById, fileDataCollectionKey, file.fileDataId);
            }
        }

        yield put(selectFileAsyncAction.success({ id: action.payload.file.id, fileData: fileData }))
    } catch (e: any) {
        yield put(selectFileAsyncAction.failure({ error: e }))
    }
}

function* checkFilesSaga(action: ReturnType<typeof checkFilesAsyncAction.request>) {
    const folderIds = action.payload.folderIds
    let checkedFileIds: ICheckedFileIds
    if (action.payload.disableUncheck) {
        checkedFileIds = { folderIds: [], fileIds: [] }
    } else {
        checkedFileIds = yield select(selectCheckedFileIds);
    }
    let files: IdObjectsWithId<IFile> = yield select(selectFiles);
    if (Object.keys(files.byId).length === 0) {
        yield take(fetchAllFilesAsyncActions.success)
        files = yield select(selectFiles);
    }

    // check files based on each folder id
    const newCheckedFileIds = folderIds.reduce((result: ICheckedFileIds, id): ICheckedFileIds => {
        const file = files.byId[id];
        if (!file) return result;
        if (file.type === FileType.FILE) return result;
        const ids = findAllCheckedIds(id, result.fileIds, files);
        // if it's check or uncheck;
        if (ids.includes(id)) {
            result.folderIds.push(id)
        } else {
            result.folderIds = result.folderIds.filter(fid => {
                return fid != id;
            })
        }
        result.fileIds = ids
        return result;
    }, { ...checkedFileIds });

    let htmlFolderId = '', sassFolderId = '';
    newCheckedFileIds.folderIds.forEach(id => {
        const file = files.byId[id];
        if (file && /^\/.*\/html\//.test(file.path)) {
            htmlFolderId = id;
        } else if (file && /^\/.*\/scss\//.test(file.path)) {
            sassFolderId = id;
        }
    });
    yield put(modifyingActiveBannerTemplateAction({ bannerTemplate: { htmlFolderId: htmlFolderId, sassFolderId: sassFolderId } }))

    yield put(checkFilesAsyncAction.success({ checkedFileIds: newCheckedFileIds }))
    yield put(loadDataForFilesAsyncAction.request({
        fileIds: newCheckedFileIds.fileIds
    }))

}

function* fileSystemActionWatcher() {
    yield takeEvery(fetchAllFilesAsyncActions.request, fetchAllFilesSaga);
    yield takeEvery(fetchFileRootWithChildrenAsyncActions.request, fetchRootWithChildrenSaga);
    yield takeEvery(fetchFileChildrenAsyncActions.request, fetchChildren);
    yield takeEvery(addFileAsyncActions.request, addFileSaga);
    yield takeEvery(renameFileAsyncActions.request, renameFileSaga);
    yield takeEvery(moveFileAsyncActions.request, moveFileSaga);
    yield takeEvery(deleteFileAsyncActions.request, deleteFileSaga);
    yield takeEvery(selectFileAsyncAction.request, selectFileSaga)
    yield takeEvery(checkFilesAsyncAction.request, checkFilesSaga)
}

export default fileSystemActionWatcher;